import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, MessageSquare, CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ChatConversationDrawer from '@/components/admin/ChatConversationDrawer';

interface ChatConversation {
  id: string;
  session_id: string;
  messages: any[];
  appointment_requested: boolean;
  appointment_id: string | null;
  user_info: any;
  started_at: string;
  last_activity_at: string;
  status: string;
}

export default function AdminChat() {
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchKnowledgeBase();
    fetchConversations();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_knowledge_base')
        .select('*')
        .single();

      if (error) throw error;

      if (data) {
        setKnowledgeBase(data.content);
        setLastUpdated(data.updated_at);
      }
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to load knowledge base',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .order('last_activity_at', { ascending: false });

      if (error) throw error;

      const formattedData = (data || []).map(conv => ({
        ...conv,
        messages: (conv.messages as any) || []
      }));
      setConversations(formattedData);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const handleSaveKnowledgeBase = async () => {
    setIsSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('chat_knowledge_base')
        .update({
          content: knowledgeBase,
          updated_at: new Date().toISOString(),
          updated_by: userData.user?.id,
        })
        .eq('id', (await supabase.from('chat_knowledge_base').select('id').single()).data?.id);

      if (error) throw error;

      setLastUpdated(new Date().toISOString());
      toast({
        title: 'Success',
        description: 'Knowledge base updated successfully',
      });
    } catch (error) {
      console.error('Error saving knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to save knowledge base',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setDrawerOpen(true);
  };

  const activeChats = conversations.filter(
    (c) => c.status === 'active' && 
    new Date(c.last_activity_at).getTime() > Date.now() - 30 * 60 * 1000
  );

  const recentChats = conversations.filter(
    (c) => c.status !== 'active' || 
    new Date(c.last_activity_at).getTime() <= Date.now() - 30 * 60 * 1000
  );

  const appointmentBookedChats = conversations.filter((c) => c.appointment_requested);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Clover's Chat & Knowledge Base</h1>
          <p className="text-muted-foreground">
            Manage Clover's knowledge base and monitor chat conversations
          </p>
        </div>

        {/* Knowledge Base Editor */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Clover's Knowledge Base
            </CardTitle>
            <CardDescription>
              Add custom information for Clover to reference when answering questions.
              This content is prioritized over general site information.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Textarea
                    value={knowledgeBase}
                    onChange={(e) => setKnowledgeBase(e.target.value)}
                    className="min-h-[400px] font-mono text-sm"
                    placeholder="Enter custom knowledge base content here...

Examples of what to include:
• Current business hours
• Specific policies (cancellation, payment, etc.)
• Seasonal information (tax season deadlines, holiday closures)
• Special promotions or discounts
• Frequently asked questions unique to your business
• Contact preferences
• Appointment preparation instructions
• Any other information you want the AI to prioritize"
                  />
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <div>
                      {knowledgeBase.length.toLocaleString()} characters
                      {lastUpdated && (
                        <span className="ml-4">
                          Last updated: {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveKnowledgeBase} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Knowledge Base
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Chat Activity Dashboard */}
        <Card>
          <CardHeader>
            <CardTitle>Chat Activity</CardTitle>
            <CardDescription>
              Monitor ongoing and recent chat conversations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="active">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="active">
                  <Clock className="h-4 w-4 mr-2" />
                  Active ({activeChats.length})
                </TabsTrigger>
                <TabsTrigger value="recent">
                  Recent ({recentChats.length})
                </TabsTrigger>
                <TabsTrigger value="booked">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Booked ({appointmentBookedChats.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="space-y-4">
                {activeChats.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No active chats right now</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeChats.map((chat) => (
                      <Card key={chat.id} className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">
                                  {chat.user_info?.name || 'Anonymous'}
                                </h4>
                                {chat.appointment_requested && (
                                  <Badge variant="default" className="bg-green-600">
                                    Appointment Booked ✓
                                  </Badge>
                                )}
                              </div>
                              {chat.user_info?.email && (
                                <p className="text-sm text-muted-foreground">{chat.user_info.email}</p>
                              )}
                              <p className="text-sm text-muted-foreground">
                                Started: {formatDistanceToNow(new Date(chat.started_at), { addSuffix: true })}
                              </p>
                              {chat.messages.length > 0 && (
                                <p className="text-sm line-clamp-2">
                                  {chat.messages[chat.messages.length - 1]?.content}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewConversation(chat.id)}
                            >
                              View Full Chat
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="recent">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Appointment</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentChats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                          No recent conversations
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentChats.map((chat) => (
                        <TableRow key={chat.id}>
                          <TableCell>
                            {formatDistanceToNow(new Date(chat.started_at), { addSuffix: true })}
                          </TableCell>
                          <TableCell>{chat.user_info?.name || 'Anonymous'}</TableCell>
                          <TableCell>{chat.user_info?.email || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={chat.status === 'completed' ? 'default' : 'secondary'}>
                              {chat.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {chat.appointment_requested ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewConversation(chat.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="booked">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {appointmentBookedChats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                          No appointments booked through chat yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      appointmentBookedChats.map((chat) => (
                        <TableRow key={chat.id}>
                          <TableCell>
                            {formatDistanceToNow(new Date(chat.started_at), { addSuffix: true })}
                          </TableCell>
                          <TableCell>{chat.user_info?.name || 'Anonymous'}</TableCell>
                          <TableCell>{chat.user_info?.email || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="default" className="bg-green-600">
                              Appointment Booked ✓
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewConversation(chat.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <ChatConversationDrawer
        conversationId={selectedConversationId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </AdminLayout>
  );
}
